import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Page } from '/imports/ui/Page';
import { DataTable, Column } from '/imports/ui/DataTable';
import { Input } from '/imports/ui/Input';
import { InfoIcon } from '/imports/ui/InfoIcon';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { useMethod } from '/imports/hooks';
import { formatNumber, objectKeys } from '/imports/utils';
import { getProductionMeterMetaReport, getProductionMeterProductionReport, ProductionMeterMetaReportRow, ProductionMeterProductionReportRow, } from '/imports/api/productionMeters';

type Row = ProductionMeterProductionReportRow & { _id: string, [key: string]: any };
type MetaRow = ProductionMeterMetaReportRow & { _id: string, [key: string]: any };

const ProductionMeterReportPageStyled = styled(Page)`
  padding: 0 3rem;
  padding-top: 1.5rem;
  box-sizing: border-box;
  h1 {
    margin-top: 0;
  }
  .section-heading {
    margin: 2rem 0 0.5rem 0;
    font-size: 1.3rem;
  }
  .search-wrap {
    margin-bottom: 1rem;
    max-width: 24rem;
  }
  .report-group {
    margin-bottom: 2rem;
    h2 {
      margin: 1rem 0 0.5rem 0;
      cursor: pointer;
      user-select: none;
      .collapse-indicator {
        display: inline-block;
        width: 1rem;
        margin-right: 0.25rem;
        font-size: 0.85em;
      }
    }
  }
  .row-actions, .row-select {
    display: none;
  }
`;

/**
 * Production meter report page: missing-meta and daily-production tables grouped by org.
 */
export const ProductionMeterReportPage = () => {
  const [report, loading] = useMethod(getProductionMeterProductionReport, undefined);
  const [metaReport, metaLoading] = useMethod(getProductionMeterMetaReport, undefined);
  const [collapsed, setCollapsed] = useState<{ [key: string]: boolean }>({});
  const toggleCollapsed = (key: string) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  const [search, setSearch] = useState('');
  const metaGroups = useMemo(() => {
    const out: { [orgName: string]: MetaRow[] } = {};
    for (const r of (metaReport || [])) {
      (out[r.orgName] = out[r.orgName] || []).push({ ...r, _id: r.meterId });
    }
    return out;
  }, [metaReport]);
  const filteredMetaGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return metaGroups;
    const out: { [orgName: string]: MetaRow[] } = {};
    for (const orgName of objectKeys(metaGroups)) {
      if (orgName.toLowerCase().includes(q)) { out[orgName] = metaGroups[orgName]; continue; }
      const matching = metaGroups[orgName].filter((r) => r.name?.toLowerCase().includes(q));
      if (matching.length) out[orgName] = matching;
    }
    return out;
  }, [metaGroups, search]);
  // Server pre-sorts rows by orgOrder; insertion order is preserved here.
  const metaGroupKeys = useMemo(() => objectKeys(filteredMetaGroups), [filteredMetaGroups]);
  const groups = useMemo(() => {
    const out: { [key: string]: Row[] } = {};
    for (const r of (report || [])) {
      const key = `${r.orgName}||${r.state || '—'}`;
      (out[key] = out[key] || []).push({ ...r, _id: r.meterId });
    }
    return out;
  }, [report]);
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    const out: { [key: string]: Row[] } = {};
    for (const key of objectKeys(groups)) {
      const [orgName, state] = key.split('||');
      const title = `${orgName} - ${state}`.toLowerCase();
      if (title.includes(q)) {
        out[key] = groups[key];
        continue;
      }
      const matchingRows = groups[key].filter((r) => r.name?.toLowerCase().includes(q));
      if (matchingRows.length) out[key] = matchingRows;
    }
    return out;
  }, [groups, search]);
  const groupKeys = useMemo(() => objectKeys(filteredGroups), [filteredGroups]);
  return (
    <ProductionMeterReportPageStyled>
      <h1>Production Meter Report</h1>
      <div className="search-wrap">
        <Input value={search} onChange={(v) => setSearch(v || '')} placeholder="Search by org name" />
      </div>
      {(loading || metaLoading) ? <LoadingFlex /> : null}
      {metaGroupKeys.length > 0 && <h2 className="section-heading">Missing Meta Fields</h2>}
      {metaGroupKeys.map((orgName) => {
        const rows = filteredMetaGroups[orgName];
        const key = `meta||${orgName}`;
        const metaColumns: Column<MetaRow>[] = [
          {
            field: 'name',
            label: 'Meter',
            render: (r) => (
              <>
                <Link to={`/production-meters/${r.meterId}?redirectOrgId=${r.orgId}`}>{r.name}</Link>
                {r.missingFields?.length ? <InfoIcon tip={`Missing: ${r.missingFields.join(', ')}`} /> : null}
              </>
            ),
          },
          { field: 'address', label: 'Address' },
          { field: 'timezone', label: 'Timezone' },
          { field: 'ppaRate', label: 'PPA Rate', render: (r) => formatNumber(r.ppaRate, 4) },
          { field: 'fixedPayment', label: 'Fixed Payment', render: (r) => formatNumber(r.fixedPayment, 2) },
          { field: 'forecastLen', label: 'Forecast', render: (r) => formatNumber(r.forecastLen) },
          { field: 'billingAccountNumber', label: 'Account #' },
          { field: 'commissioningDate', label: 'Commissioning', render: (r) => r.commissioningDate ? new Date(r.commissioningDate).toISOString().slice(0, 10) : '' },
          { field: 'degradationRate', label: 'Degradation', render: (r) => formatNumber(r.degradationRate, 3) },
          { field: 'ppaEscalator', label: 'PPA Esc.', render: (r) => formatNumber(r.ppaEscalator, 2) },
          { field: 'size', label: 'Size', render: (r) => formatNumber(r.size, 2) },
        ];
        const isCollapsed = !!collapsed[key];
        return (
          <div key={key} className="report-group">
            <h2 onClick={() => toggleCollapsed(key)}>
              <span className="collapse-indicator">{isCollapsed ? '▶' : '▼'}</span>
              {orgName}
            </h2>
            {!isCollapsed && (
              <DataTable
                data={rows}
                allIds={rows.map((r) => r._id)}
                columns={metaColumns}
                exportable={false}
              />
            )}
          </div>
        );
      })}
      {groupKeys.length > 0 && <h2 className="section-heading">Daily Production</h2>}
      {groupKeys.map((key) => {
        const rows = filteredGroups[key];
        const [orgName, state] = key.split('||');
        const dayColumns: Column<Row>[] = rows[0].dailyProduction.map((_, i) => ({
          field: `day${i}` as any,
          label: rows[0].dailyProduction[i].date,
          render: (r: Row) => formatNumber(r.dailyProduction[i]?.production || 0, 2),
        }));
        const columns: Column<Row>[] = [
          { field: 'name', label: 'Name' },
          ...dayColumns,
          { field: 'production1day', label: 'Last 24 hrs', render: (r) => formatNumber(r.production1day, 2) },
          { field: '_sevenDayAvg' as any, label: '7 day avg', render: (r) => formatNumber(r.production7days / 7, 2) },
          { field: 'production7days', label: 'Last 7 days', render: (r) => formatNumber(r.production7days, 2) },
          { field: 'estimated7days', label: 'Last 7 days est.', render: (r) => formatNumber(r.estimated7days, 2) },
          {
            field: '_ratio' as any,
            label: 'Ratio',
            render: (r) => {
              const avg = r.production7days / 7;
              return avg ? `${formatNumber(100 * r.production1day / avg)}%` : '-';
            },
          },
          {
            field: '_actualVsEstimated' as any,
            label: 'Last 7 day actual/estimated',
            render: (r) => r.estimated7days ? `${formatNumber(100 * r.production7days / r.estimated7days)}%` : '-',
          },
          { field: 'gaps', label: 'Gaps', render: (r) => r.gaps.length ? 'yes' : 'no' },
        ];
        const isCollapsed = !!collapsed[key];
        return (
          <div key={key} className="report-group">
            <h2 onClick={() => toggleCollapsed(key)}>
              <span className="collapse-indicator">{isCollapsed ? '▶' : '▼'}</span>
              {orgName} - {state}
            </h2>
            {!isCollapsed && (
              <DataTable
                data={rows}
                allIds={rows.map((r) => r._id)}
                columns={columns}
                exportable={false}
              />
            )}
          </div>
        );
      })}
    </ProductionMeterReportPageStyled>
  );
};
